import _ from 'lodash';
import React, {FC, Fragment, useEffect, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Plural,
  Trans,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncMoreStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {KnowledgeBaseArticleStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/knowledgeBaseArticleModel';
import {useAppInfiniteQuery} from '../../../../../../libs/shared/core/src/services/query/useAppInfiniteQuery';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {Skeleton} from '../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {useVisibility} from '../../../../../../libs/shared/ui/src/hooks/domHookHelpers';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  DateTimeFormatsEnum,
  DateTimeFormatter,
} from '../../../../../../srcCommon/formatters/dateTime/dateTimeFormatter';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AppState} from '../../../../../store/appState';
import {dataUpdatedAction} from '../../../../../store/data/dataGroupsActions';
import {buildAnalyticsAutomationQueryFromState} from '../../../../../store/panels/analytics/analyticsPanelHelpers';
import {getAnalyticsSelectedNamespace} from '../../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {renderTeammateValue} from '../../../../helpers/metricRenderers/teammateRenderer';
import {knowledgeBaseReportArticlesUpdatedQueries} from '../queries/knowledgeBaseReportArticlesUpdatedQueries';

/*
 * Props.
 */

interface KnowledgeBaseReportSidePanelArticlesUpdatedProps {
  totalArticles: number;
  onArticleSelect: ({
    articleId,
    shouldDisplayDraft,
  }: {
    articleId: number;
    shouldDisplayDraft: boolean;
  }) => void;
}

/*
 * Style.
 */

const StyledContentWrapperDiv = styled.div`
  padding: 18px 12px;
`;

const StyledTitleWrapperDiv = styled.div`
  margin-bottom: 8px;
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 20px;
`;

const StyledCardWrapperDiv = styled.div<{$hasHover?: boolean}>`
  display: flex;
  flex-flow: column;
  padding: 16px;
  border: 1px solid ${(p) => p.theme.alphas.black30};
  border-radius: 8px;
  box-shadow: 0px 1px 1px 0px ${(p) => p.theme.alphas.black10};

  ${(p) =>
    p.$hasHover &&
    css`
      &:hover {
        background: ${p.theme.alphas.black10};
      }
    `}
`;

const StyledArticleTitleWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const StyledCardTitleDiv = styled.div`
  flex: 1;
  line-height: 18px;
  font-weight: ${fontWeights.semibold};
  overflow: hidden;
`;

const StyledCardDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
  font-size: ${fontSizes.tiny};
  line-height: 14px;
  color: ${(p) => p.theme.greys.shade70};
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

/*
 * Component.
 */

export const KnowledgeBaseReportSidePanelArticlesUpdated: FC<
  KnowledgeBaseReportSidePanelArticlesUpdatedProps
> = ({totalArticles, onArticleSelect}) => {
  const filters = useAppSelector((state: AppState) => buildAnalyticsAutomationQueryFromState(state));
  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const dispatch = useAppDispatch();
  const theme = useUserTheme();
  const localize = useLocalize();
  const [isVisible, elementRef] = useVisibility<HTMLDivElement>();
  const previousIsVisible = usePrevious(isVisible);

  const {data, asyncStatus, isFetching, hasNextPage, fetchNextPage} = useAppInfiniteQuery(
    knowledgeBaseReportArticlesUpdatedQueries.byFilters(filters, namespace),
  );
  const previousAsyncStatus = usePrevious(asyncStatus);
  const isLoading = isFetching || asyncStatus === AsyncMoreStatusesEnum.LOADING;

  const teammates = useMemo(() => {
    if (!data) {
      return [];
    }
    return _(data.pages)
      .flatMap((page) => page.teammates)
      .uniqBy('id')
      .value();
  }, [data]);

  useEffect(() => {
    // We need to dispatch the teammate to the redux store for the teammate rendering component to work.
    if (
      teammates &&
      (asyncStatus === AsyncMoreStatusesEnum.ALL_LOADED || asyncStatus === AsyncMoreStatusesEnum.HAS_MORE) &&
      previousAsyncStatus === AsyncMoreStatusesEnum.LOADING
    ) {
      dispatch(dataUpdatedAction({teammates}));
    }
  }, [asyncStatus, teammates, dispatch, previousAsyncStatus]);

  useEffect(() => {
    // If it is not visible, or we are already visible or we do not have a next token.
    if (isLoading || !isVisible || (isVisible && previousIsVisible) || !hasNextPage) {
      return;
    }
    fetchNextPage();
  }, [isLoading, hasNextPage, isVisible, previousIsVisible, fetchNextPage]);

  if (!data) {
    return renderLoadingState();
  }
  return (
    <StyledContentWrapperDiv>
      <StyledTitleWrapperDiv>
        <Plural
          id="analytics-knowledge-base-side-panel-articles-count"
          value={totalArticles}
          one="# article"
          other="# articles"
        />
      </StyledTitleWrapperDiv>
      {data.pages.map((page, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={i}>
          {page.articlesUpdated.articles.map((article) => {
            const lastUpdatedAt = article.lastEditedAt || article.createdAt;
            return (
              <StyledCardWrapperDiv
                key={article.id}
                $hasHover={article.status !== KnowledgeBaseArticleStatusesEnum.DELETED}
                onClick={() => {
                  if (article.status === KnowledgeBaseArticleStatusesEnum.DELETED) {
                    return;
                  }
                  onArticleSelect({
                    articleId: article.id,
                    shouldDisplayDraft: article.status === KnowledgeBaseArticleStatusesEnum.DRAFT,
                  });
                }}
              >
                <StyledArticleTitleWrapperDiv>
                  <Icon name="articleSmall" color={theme.greys.shade70} />
                  <StyledCardTitleDiv>
                    <TooltipCoordinator render={() => article.name}>
                      <TooltipOverflowCondition>{article.name}</TooltipOverflowCondition>
                    </TooltipCoordinator>
                  </StyledCardTitleDiv>
                </StyledArticleTitleWrapperDiv>
                {article.status === KnowledgeBaseArticleStatusesEnum.DELETED && (
                  <StyledCardDetailsWrapperDiv>
                    <Icon name="warningFilledTiny" color={theme.greys.shade70} />
                    <span>
                      <Trans id="analytics-knowledge-base-side-panel-article-deleted">
                        Article has been deleted
                      </Trans>
                    </span>
                  </StyledCardDetailsWrapperDiv>
                )}
                {lastUpdatedAt && (
                  <StyledCardDetailsWrapperDiv>
                    <span>
                      <Trans id="analytics-knowledge-base-side-panel-article-updated">Last updated:</Trans>
                    </span>
                    <span>
                      <DateTimeFormatter value={lastUpdatedAt} format={DateTimeFormatsEnum.ABSOLUTE} />
                    </span>
                  </StyledCardDetailsWrapperDiv>
                )}
                {article.teammateId && (
                  <StyledCardDetailsWrapperDiv>
                    <span>
                      <Trans id="analytics-knowledge-base-side-panel-article-last-edited-by">
                        Last edited by:
                      </Trans>
                    </span>
                    <span>
                      {renderTeammateValue({
                        id: article.teammateId,
                        size: VisualSizesEnum.SMALL,
                        value: localize(
                          defineMessage({
                            id: 'analytics-knowledge-base-side-panel-article-last-edited-by-fallback',
                            message: 'Unknown teammate',
                          }),
                        ),
                      })}
                    </span>
                  </StyledCardDetailsWrapperDiv>
                )}
              </StyledCardWrapperDiv>
            );
          })}
        </Fragment>
      ))}
      {isLoading && (
        <>
          {renderLoadingCard()}
          {renderLoadingCard()}
          {renderLoadingCard()}
        </>
      )}
      <div ref={elementRef} />
    </StyledContentWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderLoadingState() {
  return (
    <StyledContentWrapperDiv>
      <StyledTitleWrapperDiv>
        <Skeleton height="20px" width="35%" />
      </StyledTitleWrapperDiv>
      {renderLoadingCard()}
      {renderLoadingCard()}
      {renderLoadingCard()}
    </StyledContentWrapperDiv>
  );
}

function renderLoadingCard() {
  return (
    <StyledCardWrapperDiv>
      <StyledArticleTitleWrapperDiv>
        <Skeleton height="20px" width="100%" />
      </StyledArticleTitleWrapperDiv>
      <StyledCardDetailsWrapperDiv>
        <Skeleton height="14px" width="45%" />
      </StyledCardDetailsWrapperDiv>
      <StyledCardDetailsWrapperDiv>
        <Skeleton height="14px" width="75%" />
      </StyledCardDetailsWrapperDiv>
    </StyledCardWrapperDiv>
  );
}
